import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { RotateLoader } from "react-spinners";
import { GrLinkPrevious } from "react-icons/gr";

import { AuthContext } from "../../context/main/auth-context";
import "../../css/orders/orders.css";

import OrdersHeader from "../../components/orders/orders-header";
import OrderListItemsManualOrderShipStep3 from "../../components/orders/order-list-item-manual-order-ship-step-3";
import AssistantToggleButton from "../../components/main/assistant-toggle-button";
import { PDFDocument } from "pdf-lib";

// DYMO (bewust uitgeschakeld in UI, maar code gelaten voor later)
// import { DymoPrintersList, printLabels } from "../../components/dymo/printHandler";

const OrdersManualAddedStep3 = () => {
  const auth = useContext(AuthContext); // niet direct gebruikt hier, maar laten staan
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState("");
  const [loadingState, setLoadingState] = useState("");
  const [checkedOrders, setCheckedOrders] = useState([]); // array
  const [labelsPDF, setLabelsPDF] = useState({ ResponseShipments: [] }); // PostNL batch result
  const [orderActionsAvailable, setOrderActionsAvailable] = useState(false);

  // DYMO (optioneel)
  // const printers = DymoPrintersList();
  // const [currentPrinter, setCurrentPrinter] = useState(null);
  // const [twinTurboRoll, setTwinTurboRoll] = useState("Left");
  // const [currentLabel, setCurrentLabel] = useState("label99010");

  useEffect(() => {
    setLoadingState("Bestellingen worden geladen…");
    try {
      const labelsPDFProp = JSON.parse(sessionStorage.getItem("labelsPDF") || "{}");
      setLabelsPDF(
        labelsPDFProp && typeof labelsPDFProp === "object"
          ? labelsPDFProp
          : { ResponseShipments: [] }
      );

      const checkedOrdersProp = JSON.parse(sessionStorage.getItem("checkedOrders") || "[]");
      const shippingMethodProp = JSON.parse(sessionStorage.getItem("shippingMethod") || "null");

      const ordersArr = Array.isArray(checkedOrdersProp) ? checkedOrdersProp : [];
      setCheckedOrders(ordersArr);

      if (ordersArr.length > 0 && shippingMethodProp) {
        setOrderActionsAvailable(true);
      } else {
        setOrderActionsAvailable(false);
      }
    } catch (e) {
      setErrorMessage("Kon verzendgegevens niet laden.");
    } finally {
      setLoadingState("");
    }
  }, []);

  // Zijn er überhaupt labels die geprint kunnen worden?
  const hasPrintableLabels = useMemo(() => {
    const shipments = labelsPDF?.ResponseShipments;
    if (!Array.isArray(shipments) || shipments.length === 0) return false;
    return shipments.some((s) => Array.isArray(s?.Labels) && s.Labels.length > 0);
  }, [labelsPDF]);

  // Helpers ----------------------------------------------------

  const base64ToArrayBuffer = (data) => {
    const binaryString = window.atob(data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes;
  };

  const uint8ArrayToBase64 = (bytes) => {
    let binary = "";
    const chunk = 8192;
    for (let i = 0; i < bytes.length; i += chunk) {
      const slice = bytes.subarray(i, i + chunk);
      binary += String.fromCharCode(...slice);
    }
    return btoa(binary);
  };

  const collectLabelBase64Pages = () => {
    const pages = [];
    const shipments = labelsPDF?.ResponseShipments || [];
    shipments.forEach((shipment) => {
      const labels = shipment?.Labels || [];
      labels.forEach((label) => {
        if (label?.Content) pages.push(label.Content);
      });
    });
    return pages;
  };

  const mergePDFsToBase64 = async () => {
    const pages = collectLabelBase64Pages();
    if (pages.length === 0) return null;

    const mergedPDF = await PDFDocument.create();

    for (const base64PDF of pages) {
      const pdfBytes = base64ToArrayBuffer(base64PDF);
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const copiedPages = await mergedPDF.copyPages(pdfDoc, pdfDoc.getPageIndices());
      copiedPages.forEach((p) => mergedPDF.addPage(p));
    }

    const mergedPDFBytes = await mergedPDF.save();
    return uint8ArrayToBase64(new Uint8Array(mergedPDFBytes));
  };

  const openPdfInNewTab = (base64Data) => {
    const arrBuffer = base64ToArrayBuffer(base64Data);
    const blob = new Blob([arrBuffer], { type: "application/pdf" });

    // Edge/IE fallback
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob);
      return;
    }

    const url = URL.createObjectURL(blob);
    const tab = window.open(url);

    // auto-revoke URL
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);

    // sommige browsers hebben geen onload op window.open blobs
    if (!tab) {
      alert("Sta pop-ups toe om labels te openen.");
    }
  };

  const handlePreviewLabels = async () => {
    if (!hasPrintableLabels) return;
    try {
      setLoadingState("Labels samenvoegen…");
      const b64 = await mergePDFsToBase64();
      if (!b64) return;
      openPdfInNewTab(b64);
    } catch (e) {
      setErrorMessage("Kon labels niet openen.");
    } finally {
      setLoadingState("");
    }
  };

  const handlePrint = async () => {
    if (!hasPrintableLabels) return;
    try {
      setLoadingState("Labels voorbereiden voor print…");
      const b64 = await mergePDFsToBase64();
      if (!b64) return;

      // base64 -> Blob -> objectURL -> print
      const bytes = base64ToArrayBuffer(b64);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const printWindow = window.open(url);
      if (printWindow) {
        // Soms is onload niet betrouwbaar; kleine delay werkt in de praktijk goed
        const tryPrint = () => {
          try {
            printWindow.focus();
            printWindow.print();
          } catch (e) {
            // probeer nog een keer
            setTimeout(tryPrint, 250);
          }
        };
        printWindow.onload = tryPrint;
      } else {
        alert("Sta pop-ups toe om te kunnen printen.");
      }

      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setErrorMessage("Printen van labels is mislukt.");
    } finally {
      setLoadingState("");
    }
  };

  // UI --------------------------------------------------------

  return (
    <React.Fragment>
      <div className="title-header title-header-ship-orders">
        <span>Bestellingen verzonden</span>
        <div className="shipmentSteps">
          <div className="shipmentOneStep shipmentFirstStep finished">
            <div className="shipmentOneStepCircle">1</div>
            <div className="">Verzendmethode</div>
          </div>
          <div className="shipmentStepsLine"></div>
          <div className="shipmentOneStep finished">
            <div className="shipmentOneStepCircle">2</div>
            <div className="">Overzicht</div>
          </div>
          <div className="shipmentStepsLine"></div>
          <div className="shipmentOneStep finished">
            <div className="shipmentOneStepCircle">3</div>
            <div className="">Bevestiging</div>
          </div>
        </div>
      </div>

      <OrdersHeader recountOrders={false} />

      {!!errorMessage && <div className="errorMessageField">{errorMessage}</div>}

      {checkedOrders.length > 0 && (
        <div className="orders-container-header ship-orders-container-header">
          <div className="orders-container-header-actions ship-orders-container-header-actions shipped-orders-container-header-actions">
            <div className="confirmedOrderPrintOptions">
              <Button
                disabled={!hasPrintableLabels}
                className="viewLabelsButton"
                variant="secondary"
                onClick={handlePreviewLabels}
              >
                Labels bekijken
              </Button>
              <Button
                disabled={!hasPrintableLabels}
                className="viewLabelsButton"
                variant="primary"
                onClick={handlePrint}
              >
                Print labels
              </Button>
            </div>
          </div>
        </div>
      )}

      {checkedOrders.length > 0 &&
        checkedOrders.map((order, index) => {
          const key = order.id || order.orderId || order._id || index;
          return (
            <OrderListItemsManualOrderShipStep3
              order={order}
              key={key}
              index={index}
            />
          );
        })}

      <div className="orders-container-footer-actions ship-orders-container-footer-actions">
        <Button
          onClick={() => {
            navigate("/orders/manual-added");
            window.scrollTo(0, 0);
          }}
        >
          Terug naar bestellingen
        </Button>
      </div>

      {loadingState && (
        <div className="loading-container">
          <div className="loading-container-title">{loadingState}</div>
          <RotateLoader
            size={100}
            width={100}
            height={10}
            color={"#fff"}
            loading={!!loadingState}
          />
        </div>
      )}

      <AssistantToggleButton />
    </React.Fragment>
  );
};

export default OrdersManualAddedStep3;
